import { useState } from 'react';

import { Center, SimpleGrid, Spinner, Text } from '@chakra-ui/react';

import { Individual, PayCalendar, Saving } from '@aslanpay/portal-common-lib';

import { ActiveSavingStatuses } from 'app/constants/savings';
import useFetchSavings from 'app/hooks/useFetchSavings';

import SavingPotCard from './SavingPotCard';
import { localToday } from './savingFormatters';
import SavingPotDrawer from './SavingPotDrawer';

interface IndividualSavingsPotsProps {
  individual?: Individual;
  payCalendar?: PayCalendar;
  today?: string;
}

const byActiveThenNewest = (a: Saving, b: Saving) => {
  const aActive = ActiveSavingStatuses.includes(a.status);
  const bActive = ActiveSavingStatuses.includes(b.status);
  if (aActive !== bActive) {
    return aActive ? -1 : 1;
  }
  return (b.startDate || b.createdDate || '').localeCompare(a.startDate || a.createdDate || '');
};

function IndividualSavingsPots({
  individual = undefined,
  payCalendar = undefined,
  today = localToday(),
}: IndividualSavingsPotsProps) {
  const [openSaving, setOpenSaving] = useState<Saving>();

  const { data: savings, isLoading } = useFetchSavings(
    { individualIds: [individual?.id || ''], page: 0, size: 100 },
    !!individual?.id
  );

  if (isLoading) {
    return (
      <Center py="20px">
        <Spinner color="brand.purple" />
      </Center>
    );
  }

  if (!savings?.list.length) {
    return (
      <Text textStyle="smallRegular" color="brand.darkGray">
        No savings pots yet
      </Text>
    );
  }

  return (
    <>
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing="16px" w="full">
        {[...savings.list].sort(byActiveThenNewest).map((saving) => (
          <SavingPotCard key={saving.id} saving={saving} onOpen={setOpenSaving} />
        ))}
      </SimpleGrid>
      {openSaving && (
        <SavingPotDrawer
          saving={openSaving}
          payCalendar={payCalendar}
          today={today}
          onClose={() => setOpenSaving(undefined)}
        />
      )}
    </>
  );
}

export default IndividualSavingsPots;
